import { useSyncExternalStore } from "react";

import store from "@src/store";
import { IgGrayPrimitives } from "@intelligems/ig-design-tokens/constants";
import { Box } from "@mui/material";
import { IgCopyTextTypography } from "@intelligems/ig-theme/IgCopyTextTypography";
import { XIcon } from "lucide-react";

import { DESIGN_TOKENS, REACT_GRAB_IGNORE_ATTR } from "../constants";
import { ToolbarButton } from "./ToolbarButton";

const COLOR = {
  bg: IgGrayPrimitives.A600,
  key: IgGrayPrimitives[900],
  value: IgGrayPrimitives[300],
  muted: IgGrayPrimitives.A200,
};

const MONO = { fontFamily: "monospace", fontSize: 12, lineHeight: 1.8 };

const Punct = ({ children }: { children: React.ReactNode }) => (
  <Box component="span" sx={{ color: COLOR.muted }}>
    {children}
  </Box>
);

// A clickable JSON value (click to copy the raw value). `null` when absent.
const CopyValue = ({ name, value }: { name: string; value?: string }) =>
  value == null ? (
    <Box component="span" sx={{ color: COLOR.muted }}>
      null
    </Box>
  ) : (
    <IgCopyTextTypography
      text={`"${value}"`}
      customCopyText={value}
      onDarkBackground
      copiedTooltip={() => `Copied ${name}`}
      typographyProps={{ sx: { ...MONO, color: COLOR.value } }}
    />
  );

const Row = ({ name, value }: { name: string; value?: string }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pl: 2 }}>
    <Box component="span" sx={{ color: COLOR.key }}>{`"${name}"`}</Box>
    <Punct>:</Punct>
    <CopyValue name={name} value={value} />
    <Punct>,</Punct>
  </Box>
);

type VariablesPanelProps = {
  onClose: () => void;
};

/**
 * Dev-only panel listing the IDs we most often reach for while debugging
 * Intelligems, rendered as a JSON object whose values are click-to-copy.
 *
 * Values are read from the Redux `store` singleton (works outside the provider
 * tree where <Devtools /> mounts); we subscribe so they stay live as the user
 * navigates. Reuses the shared bottom-panel position via DESIGN_TOKENS.panel.
 */
export const VariablesPanel = ({ onClose }: VariablesPanelProps) => {
  // Subscribe to the Redux store so values refresh on navigation (the app
  // dispatches setExperience/resetExperience etc. as routes change). Using
  // useSyncExternalStore (not a manual subscribe + forceUpdate) makes `state` a
  // tracked reactive value — otherwise the React Compiler memoizes the JSX and
  // skips updates, since `store.getState()` is an external read it can't see.
  const state = useSyncExternalStore(store.subscribe, store.getState);
  const organizationId = state.organization.organization?.id;
  const shopId = state.organization.organization?.shopId;
  const experienceId = state.experience.experience?.id;
  const variationIds = state.variations.variations?.map((v) => v.id) ?? [];

  return (
    <Box {...{ [REACT_GRAB_IGNORE_ATTR]: "" }} sx={DESIGN_TOKENS.panel}>
      <Box
        sx={{
          height: "100%",
          overflow: "auto",
          background: COLOR.bg,
          color: COLOR.key,
          padding: 2,
          ...MONO,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 1,
          }}
        >
          <Box component="span" sx={{ color: COLOR.muted }}>
            Variables
          </Box>
          <ToolbarButton onClick={onClose} title="Close">
            <XIcon size={12} />
          </ToolbarButton>
        </Box>

        <Punct>{"{"}</Punct>

        <Row name="organizationId" value={organizationId} />
        <Row name="shopId" value={shopId} />
        <Row name="experienceId" value={experienceId} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pl: 2 }}>
          <Box component="span" sx={{ color: COLOR.key }}>
            "variationIds"
          </Box>
          <Punct>: [{variationIds.length === 0 ? "]" : ""}</Punct>
        </Box>
        {variationIds.length > 0 && (
          <>
            {variationIds.map((id, i) => (
              <Box
                key={id}
                sx={{ display: "flex", alignItems: "center", pl: 4 }}
              >
                <CopyValue name={`variationIds[${i}]`} value={id} />
                {i < variationIds.length - 1 && <Punct>,</Punct>}
              </Box>
            ))}
            <Box sx={{ pl: 2 }}>
              <Punct>]</Punct>
            </Box>
          </>
        )}

        <Punct>{"}"}</Punct>
      </Box>
    </Box>
  );
};
